import FormInput from '../../../components/FormInput';
import FormEntry from '../../../components/FormEntry';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import SectionHeader from 'components/SectionHeader';
import { getConfigs } from '../../../api/configs';
import { getSingletonConfigCalibrationId } from '../../../hooks/useSingletonSessions';
import { CalibrationRefType, SessionAnalysisTypeUtils, SessionAnalysisType } from '../../../types/SessionAnalysisType';
import { useSessions, useSessionCalcData } from '../../../hooks/useSessions';
import { useChainModels } from '../../../hooks/useChainModels';
import { useUser } from '../../../hooks/useUser';

type SelectionMode = 'team' | 'session';

export default function NewSessionCalibrationConfig(props: {
  ridersById: Map<any, any>;
  onRiderWarningChange?: (show: boolean) => void;
  basicMode?: boolean;
}) {
  const { ridersById, basicMode } = props;
  const { watch, setValue, register, getValues } = useFormContext();
  // A restored form (route-picker round trip) carries source_session_id when
  // the user was in "Select from Session" mode — re-derive the radio and the
  // selected session from it instead of resetting to team mode.
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(
    () => (getValues('source_session_id') ? 'session' : 'team'),
  );
  const { data: chainModels } = useChainModels();
  const [userProfile] = useUser();
  const teamId: string | null = userProfile?.team_id ?? null;

  // Watch for analysis_type to determine if indoor session
  const analysisType = watch('analysis_type');
  const isIndoorSession = Number(analysisType) >= SessionAnalysisType.TRAVERSE_INDOORS;

  // For Team Calibration mode
  const [teamCalibrationConfig, setTeamCalibrationConfig] = useState<any>(null);
  // Surface team-calibration load failures (instead of an eternal "Loading...") and allow retry.
  const [calibrationError, setCalibrationError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // For Session/Config mode
  const [selectedSessionId, setSelectedSessionId] = useState<string>(
    () => getValues('source_session_id') || '',
  );
  const [availableConfigs, setAvailableConfigs] = useState<any[]>([]);
  // Previous session id, so the clear-calibration effect fires only on an
  // actual session CHANGE — not on mount with a restored session, and not on
  // background calc-data refetches.
  const prevSessionIdRef = useRef(selectedSessionId);

  // Fetch data
  const { data: sessions } = useSessions();
  // Only fetch session calc data if we have a valid selected session ID
  // Pass a dummy ID if none selected to prevent API call with empty ID
  const { data: selectedSessionCalcData } = useSessionCalcData(selectedSessionId || 'no-session-selected');

  // Rider mass and suggested combined mass
  const EQUIPMENT_MASS = 8.5;
  const watchedRiderId = watch('rider_id');
  const riderMass = watchedRiderId
    ? ridersById.get(watchedRiderId)?.mass || 0
    : 0;
  const suggestedMass = riderMass + EQUIPMENT_MASS;

  // Set default front_tooth_count and magnet count on mount. Both are
  // overwritten below by the source calibration's values when it has them.
  useEffect(() => {
    setValue('front_tooth_count', 56);
    setValue('wheel_magnet_count', 1);
  }, [setValue]);

  // Auto-set chain model to team default, or system default
  useEffect(() => {
    if (!chainModels?.length) return;
    const teamDefault = chainModels.find(
      (m) => m.is_default && m.team_id === teamId,
    );
    const systemDefault = chainModels.find(
      (m) => m.is_default && m.team_id === null,
    );
    const defaultModel = teamDefault || systemDefault;
    if (defaultModel) {
      setValue('model_chain_id', defaultModel.id);
    }
  }, [chainModels, teamId, setValue]);

  // Update combined_mass when rider selection changes
  useEffect(() => {
    if (riderMass > 0) {
      setValue(
        'combined_mass',
        riderMass + EQUIPMENT_MASS,
        { shouldValidate: true },
      );
    }
  }, [riderMass, setValue]);

  // Handle mode change
  const handleModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode);
    // Clear form values when switching modes
    setValue('calibration_id', '', { shouldValidate: false });
    setValue('combined_mass', '', { shouldValidate: false });
    setValue('crr', '', { shouldValidate: false });
    setValue('source_session_id', '', { shouldValidate: false }); // Clear the session dropdown
    setSelectedSessionId('');
    setAvailableConfigs([]);
  };

  // Fetch the team calibration config when in team mode.
  // We fetch ONLY the team calibration by id (GET /configs?id=<id>, ~25 ms) rather than the
  // whole team's config list (GET /configs — multi-second, multi-MB), which previously gated
  // this effect and left the panel stuck on "Loading..." until that giant payload resolved.
  // Re-runs when `retryCount` is bumped by the Retry button.
  useEffect(() => {
    if (selectionMode !== 'team') return;

    let cancelled = false;
    const fetchTeamCalibration = async () => {
      try {
        const teamCalibrationId = await getSingletonConfigCalibrationId(CalibrationRefType.TEAM);
        const [teamConfig] = await getConfigs([teamCalibrationId]);
        if (!teamConfig) {
          throw new Error('Team calibration not found in available configs');
        }
        if (cancelled) return;

        setTeamCalibrationConfig(teamConfig);
        setCalibrationError(null);
        // Automatically set the calibration_id and related fields
        setValue('calibration_id', teamConfig.id, { shouldValidate: true });
        const mass = riderMass > 0
          ? riderMass + EQUIPMENT_MASS
          : teamConfig.combined_mass;
        setValue('combined_mass', mass, { shouldValidate: true });
        setValue('crr', teamConfig.crr, { shouldValidate: true });
        if (teamConfig.model_chain_id) {
          setValue('model_chain_id', teamConfig.model_chain_id);
        }
        const ftc = teamConfig.drivetrain?.front_tooth_count;
        if (ftc) {
          setValue('front_tooth_count', ftc);
        }
        if (teamConfig.wheel_magnet_count) {
          setValue('wheel_magnet_count', teamConfig.wheel_magnet_count);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching team calibration:', error);
        setCalibrationError(
          error instanceof Error ? error.message : 'Failed to load team calibration',
        );
      }
    };

    fetchTeamCalibration();
    return () => {
      cancelled = true;
    };
  }, [setValue, selectionMode, retryCount]);

  // Watch for session selection changes
  useEffect(() => {
    if (selectionMode !== 'session') return;

    const subscription = watch((value, { name }) => {
      if (name === 'source_session_id' && value.source_session_id) {
        // Session field changed: value.source_session_id
        setSelectedSessionId(value.source_session_id);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, selectionMode]);

  // Update available configs when session is selected (for session mode)
  useEffect(() => {
    if (selectionMode !== 'session') return;

    if (selectedSessionId && selectedSessionId !== 'no-session-selected') {
      // Session selected: selectedSessionId
      // Session data received: selectedSessionCalcData

      if (selectedSessionCalcData && selectedSessionCalcData.configs) {
        // Configs found: selectedSessionCalcData.configs.length configs
        setAvailableConfigs(selectedSessionCalcData.configs);
      } else {
        // No configs found for session: selectedSessionId
        setAvailableConfigs([]);
      }
    }

    // Clear calibration selection when the session actually changes (a mount
    // with a restored session, or a calc-data refetch, is not a change)
    if (selectedSessionId && prevSessionIdRef.current !== selectedSessionId) {
      setValue('calibration_id', '', { shouldValidate: false });
      setValue('combined_mass', '', { shouldValidate: false });
      setValue('crr', '', { shouldValidate: false });
    }
    prevSessionIdRef.current = selectedSessionId;
  }, [selectedSessionCalcData, selectedSessionId, setValue, selectionMode]);

  // Watch for changes in calibration selection (for session mode)
  useEffect(() => {
    if (selectionMode !== 'session') return;

    const subscription = watch((value: any, info: any) => {
      if (info.name === 'calibration_id' && value.calibration_id) {
        const selectedCal = availableConfigs.find(
          (c) => c.id === value.calibration_id,
        );
        if (selectedCal) {
          const mass = riderMass > 0
            ? riderMass + EQUIPMENT_MASS
            : selectedCal.combined_mass;
          setValue('combined_mass', mass, {
            shouldValidate: true,
          });
          setValue('crr', selectedCal.crr, {
            shouldValidate: true,
          });
          if (selectedCal.model_chain_id) {
            setValue('model_chain_id', selectedCal.model_chain_id);
          }
          const ftc =
            selectedCal.drivetrain?.front_tooth_count;
          if (ftc) {
            setValue('front_tooth_count', ftc);
          }
          if (selectedCal.wheel_magnet_count) {
            setValue(
              'wheel_magnet_count',
              selectedCal.wheel_magnet_count,
            );
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, availableConfigs, selectionMode]);

  return (
    <div className="cr-mb-4 cr-flex cr-w-full cr-flex-[1_1] cr-flex-col">
      <div className="cr-h-full cr-w-full">
        <div className="cr-flex cr-flex-col">
          <SectionHeader title={
            basicMode
              ? 'Session Parameters'
              : 'Calibration Configuration'
          } />

          <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
            {/* Radio buttons for selection mode */}
            {!basicMode && (
              <FormEntry label="Copy Configuration From">
                <div className="cr-flex cr-flex-col cr-gap-2">
                  <label className={
                    'cr-flex cr-cursor-pointer '
                    + 'cr-items-center'
                  }>
                    <input
                      type="radio"
                      name="selectionMode"
                      value="team"
                      checked={selectionMode === 'team'}
                      onChange={() => handleModeChange('team')}
                      className="cr-mr-2"
                    />
                    <span>Default Team Calibration</span>
                  </label>
                  <label className={
                    'cr-flex cr-cursor-pointer '
                    + 'cr-items-center'
                  }>
                    <input
                      type="radio"
                      name="selectionMode"
                      value="session"
                      checked={selectionMode === 'session'}
                      onChange={() => handleModeChange('session')}
                      className="cr-mr-2"
                    />
                    <span>Select from Session</span>
                  </label>
                </div>
              </FormEntry>
            )}

            {/* Team Calibration Mode */}
            {!basicMode && selectionMode === 'team' && (
              <FormEntry label="Calibration">
                <div className={
                  'cr-flex cr-items-center cr-justify-between cr-rounded-md '
                  + 'cr-border cr-border-gray-300 '
                  + 'cr-bg-gray-50 cr-px-3 cr-py-2'
                }>
                  <span className={
                    calibrationError && !teamCalibrationConfig
                      ? 'cr-text-red-600'
                      : 'cr-text-gray-700'
                  }>
                    {teamCalibrationConfig
                      ? 'Team Calibration: '
                        + `${teamCalibrationConfig.description} `
                        + '('
                        + `${teamCalibrationConfig.combined_mass}`
                        + ' kg, CRR: '
                        + `${teamCalibrationConfig.crr})`
                      : calibrationError
                        ? 'Could not load team calibration'
                        : 'Loading Team Calibration...'}
                  </span>
                  {calibrationError && !teamCalibrationConfig && (
                    <button
                      type="button"
                      onClick={() => setRetryCount((c) => c + 1)}
                      className={
                        'cr-ml-2 cr-rounded cr-border cr-border-gray-300 '
                        + 'cr-bg-white cr-px-2 cr-py-1 cr-text-sm '
                        + 'cr-text-gray-700 hover:cr-bg-gray-100'
                      }
                    >
                      Retry
                    </button>
                  )}
                </div>
                <input
                  type="hidden"
                  {...register('calibration_id', {
                    required: 'Calibration is Required',
                  })}
                />
              </FormEntry>
            )}

            {/* Hidden calibration_id for basic mode */}
            {basicMode && (
              <input
                type="hidden"
                {...register('calibration_id')}
              />
            )}

            {/* Session/Config Mode */}
            {!basicMode && selectionMode === 'session' && (
              <>
                <FormEntry label="Select Session">
                  <FormInput
                    fieldName="source_session_id"
                    options={Array.from(sessions || [])
                      .filter((session) => (
                        session.analysis_type
                          >= SessionAnalysisTypeUtils
                            .singletonLevel()
                      ))
                      .reverse()}
                    optionLabelFn={(session) => (
                      `${session.date} - ${session.description}`
                    )}
                    emptyOption="Select a Session"
                    customClass="cr-w-full md:cr-w-128"
                    required="Session is Required"
                  />
                </FormEntry>

                {selectedSessionId && (
                  <FormEntry label="Select Configuration">
                    <FormInput
                      fieldName="calibration_id"
                      options={availableConfigs}
                      optionLabelFn={(config) => (
                        `${config.letter} - `
                        + `${config.description} `
                        + `(${config.combined_mass} kg, `
                        + `CRR: ${config.crr})`
                      )}
                      emptyOption="Select a Configuration"
                      customClass="cr-w-full md:cr-w-128"
                      required="Configuration is Required"
                    />
                  </FormEntry>
                )}
              </>
            )}

            {/* Common fields */}
            <FormEntry label="Combined Mass">
              <div className="cr-flex cr-flex-col cr-gap-1">
                {riderMass > 0 && (
                  <>
                    <div className="cr-flex cr-gap-6 cr-text-sm">
                      <span className="cr-font-semibold">
                        Total
                      </span>
                      <span className="cr-font-semibold">
                        Rider
                      </span>
                      <span className="cr-font-semibold">
                        Bike &amp; Equip.
                      </span>
                    </div>
                    <div className="cr-text-sm cr-text-gray-600">
                      {suggestedMass.toFixed(1)}
                      {' (suggested) = '}
                      {riderMass} + {EQUIPMENT_MASS}
                    </div>
                  </>
                )}
                <div className="cr-flex cr-items-center cr-gap-2">
                  <FormInput
                    fieldName="combined_mass"
                    required="Combined Mass is Required"
                    inputType="number"
                    customClass="cr-w-24"
                  />
                  <span className="cr-text-sm">kg</span>
                  <span className="cr-text-sm cr-font-semibold">
                    Actual
                  </span>
                </div>
              </div>
            </FormEntry>
            <FormEntry label="CRR">
              <FormInput fieldName="crr" inputType="number" />
            </FormEntry>

            {/* Indoor Session Parameters */}
            {!basicMode && isIndoorSession && (
              <FormEntry label="Wheel Circumference (m)">
                <FormInput
                  fieldName="wheel_circumference"
                  inputType="number"
                  defaultValue={2.1}
                />
              </FormEntry>
            )}

            {!basicMode && (
              <>
                <FormEntry label="Front Chain Teeth">
                  <FormInput
                    fieldName="front_tooth_count"
                    inputType="number"
                    customClass="cr-w-24"
                  />
                </FormEntry>

                <FormEntry label="Magnet Count">
                  <FormInput
                    fieldName="wheel_magnet_count"
                    inputType="number"
                    customClass="cr-w-24"
                  />
                </FormEntry>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
